import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { AlertCircle } from 'lucide-react'
import { GET_CUSTOMER_CARTS } from './B2BCartsQueries'
import { PageContainer } from '@/components/shared/page-container'
import { HeaderSection } from '@/components/shared/header-section'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatDate } from '@/utils/dateUtils'

const LoadingSkeleton = () => (
  <PageContainer>
    <Card className="p-6">
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
      </div>
    </Card>
  </PageContainer>
)

const ErrorDisplay = ({ message }: { message: string }) => (
  <PageContainer>
    <Card className="p-6">
      <div className="flex items-center text-red-500 gap-2">
        <AlertCircle className="h-5 w-5" />
        <span>Error loading carts: {message}</span>
      </div>
    </Card>
  </PageContainer>
)

export default function CustomerCarts() {
  const { accountId } = useParams()
  const navigate = useNavigate()

  const { data, loading, error } = useQuery(GET_CUSTOMER_CARTS, {
    variables: { customerId: Number(accountId) },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  })

  if (loading) return <LoadingSkeleton />
  if (error) return <ErrorDisplay message={error.message} />

  const carts = data?.getCustomerCarts || []

  return (
    <PageContainer>
      <HeaderSection
        title={`Carts for account #${accountId}`}
        subtitle="View this customer's saved carts and their contents"
      />
      <div className="mt-8">
        <Card>
          <Table className="text-sm">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="text-right">Items</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {carts.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-gray-500 py-8"
                  >
                    No carts found for this account.
                  </TableCell>
                </TableRow>
              ) : (
                carts.map((cart: any) => (
                  <TableRow key={cart.id}>
                    <TableCell className="font-medium">
                      {cart.name ? (
                        cart.name
                      ) : (
                        <span className="italic text-gray-400">
                          Untitled cart
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {cart.itemCount}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {cart.isActive && (
                          <Badge variant="green" className="text-xs">
                            Active
                          </Badge>
                        )}
                        {cart.isModified && (
                          <Badge variant="lightBlue" className="text-xs">
                            Modified
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(cart.updatedAt)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="link"
                        className="p-0 h-auto text-xs text-blue-500"
                        onClick={() =>
                          navigate(
                            `/dashboard/b2b-accounts/${accountId}/carts/${cart.id}`
                          )
                        }
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </PageContainer>
  )
}
